import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { createJob, getJob, updateJob } from '../../services/jobs';
import type { Job, JobType, ExperienceLevel, LocationType } from '../../types/job';
import {
  JOB_TYPE_LABELS,
  EXPERIENCE_LEVEL_LABELS,
  LOCATION_TYPE_LABELS,
  JOB_CATEGORIES,
} from '../../types/job';

export function PostJobPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Partial<Job>>({
    title: '',
    description: '',
    job_type: 'full_time',
    experience_level: 'mid',
    category: 'Development',
    location_type: 'remote',
    location_country: 'Ireland',
    salary_currency: 'EUR',
    salary_period: 'year',
    show_salary: true,
  });

  useEffect(() => {
    if (isEditing && id) {
      loadJob(id);
    }
  }, [id, isEditing]);

  const loadJob = async (jobId: string) => {
    try {
      const data = await getJob(jobId);
      setForm(data);
    } catch (err) {
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const updateField = <K extends keyof Job>(field: K, value: Job[K] | undefined) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent, publish = false) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (isEditing && id) {
        await updateJob(id, form);
        if (publish) {
          // TODO: Publish after update
        }
      } else {
        const newJob = await createJob(form);
        if (publish) {
          // TODO: Publish immediately
        }
        navigate(`/company/jobs/${newJob.id}`);
        return;
      }
      navigate('/company/jobs');
    } catch (err: any) {
      console.error('Failed to save job:', err);
      setError(err.response?.data?.error || 'Failed to save job. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-8" />
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
              <div className="h-10 bg-gray-200 rounded" />
              <div className="h-32 bg-gray-200 rounded" />
              <div className="h-10 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/company/jobs"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Jobs
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Job' : 'Post New Job'}
          </h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={e => handleSubmit(e, false)}>
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h2>

            <div className="space-y-6">
              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title *
                </label>
                <input
                  type="text"
                  id="title"
                  required
                  maxLength={200}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="e.g., Senior Software Engineer"
                  value={form.title || ''}
                  onChange={e => updateField('title', e.target.value)}
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Job Description *
                </label>
                <textarea
                  id="description"
                  required
                  rows={8}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Describe the role, team, and what makes this opportunity exciting..."
                  value={form.description || ''}
                  onChange={e => updateField('description', e.target.value)}
                />
              </div>

              {/* Type & Level */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label htmlFor="job_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Job Type *
                  </label>
                  <select
                    id="job_type"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={form.job_type || ''}
                    onChange={e => updateField('job_type', e.target.value as JobType)}
                  >
                    {(Object.keys(JOB_TYPE_LABELS) as JobType[]).map(type => (
                      <option key={type} value={type}>
                        {JOB_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="experience_level" className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level *
                  </label>
                  <select
                    id="experience_level"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={form.experience_level || ''}
                    onChange={e => updateField('experience_level', e.target.value as ExperienceLevel)}
                  >
                    {(Object.keys(EXPERIENCE_LEVEL_LABELS) as ExperienceLevel[]).map(level => (
                      <option key={level} value={level}>
                        {EXPERIENCE_LEVEL_LABELS[level]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  id="category"
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={form.category || ''}
                  onChange={e => updateField('category', e.target.value)}
                >
                  {JOB_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Location</h2>

            <div className="space-y-6">
              <div>
                <label htmlFor="location_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Work Location *
                </label>
                <select
                  id="location_type"
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={form.location_type || ''}
                  onChange={e => updateField('location_type', e.target.value as LocationType)}
                >
                  {(Object.keys(LOCATION_TYPE_LABELS) as LocationType[]).map(type => (
                    <option key={type} value={type}>
                      {LOCATION_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>

              {(form.location_type === 'onsite' || form.location_type === 'hybrid') && (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="location_city" className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      id="location_city"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="e.g., Dublin"
                      value={form.location_city || ''}
                      onChange={e => updateField('location_city', e.target.value || undefined)}
                    />
                  </div>
                  <div>
                    <label htmlFor="location_country" className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      id="location_country"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={form.location_country || ''}
                      onChange={e => updateField('location_country', e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Timezone
                </label>
                <input
                  type="text"
                  id="timezone"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="e.g., GMT/UTC, CET"
                  value={form.timezone || ''}
                  onChange={e => updateField('timezone', e.target.value || undefined)}
                />
              </div>
            </div>
          </div>

          {/* Compensation */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Compensation</h2>

            <div className="space-y-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="show_salary"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={form.show_salary || false}
                  onChange={e => updateField('show_salary', e.target.checked)}
                />
                <label htmlFor="show_salary" className="ml-2 text-sm text-gray-700">
                  Display salary range to candidates
                </label>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label htmlFor="salary_min" className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Salary
                  </label>
                  <input
                    type="number"
                    id="salary_min"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="e.g., 60000"
                    value={form.salary_min || ''}
                    onChange={e => updateField('salary_min', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
                <div>
                  <label htmlFor="salary_max" className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Salary
                  </label>
                  <input
                    type="number"
                    id="salary_max"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="e.g., 90000"
                    value={form.salary_max || ''}
                    onChange={e => updateField('salary_max', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
                <div>
                  <label htmlFor="salary_period" className="block text-sm font-medium text-gray-700 mb-2">
                    Period
                  </label>
                  <select
                    id="salary_period"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={form.salary_period || 'year'}
                    onChange={e => updateField('salary_period', e.target.value)}
                  >
                    <option value="year">Per Year</option>
                    <option value="month">Per Month</option>
                    <option value="day">Per Day</option>
                    <option value="hour">Per Hour</option>
                  </select>
                </div>
              </div>

              {(form.job_type === 'contract' || form.job_type === 'freelance') && (
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Contract Details</h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <label htmlFor="contract_duration" className="block text-sm font-medium text-gray-700 mb-2">
                        Duration (months)
                      </label>
                      <input
                        type="number"
                        id="contract_duration"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={form.contract_duration_months || ''}
                        onChange={e => updateField('contract_duration_months', e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </div>
                    <div>
                      <label htmlFor="contract_rate_min" className="block text-sm font-medium text-gray-700 mb-2">
                        Min Daily Rate (€)
                      </label>
                      <input
                        type="number"
                        id="contract_rate_min"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={form.contract_rate_min || ''}
                        onChange={e => updateField('contract_rate_min', e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </div>
                    <div>
                      <label htmlFor="contract_rate_max" className="block text-sm font-medium text-gray-700 mb-2">
                        Max Daily Rate (€)
                      </label>
                      <input
                        type="number"
                        id="contract_rate_max"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={form.contract_rate_max || ''}
                        onChange={e => updateField('contract_rate_max', e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Additional Details</h2>

            <div className="space-y-6">
              <div>
                <label htmlFor="requirements" className="block text-sm font-medium text-gray-700 mb-2">
                  Requirements
                </label>
                <textarea
                  id="requirements"
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="What skills and qualifications are required?"
                  value={form.requirements || ''}
                  onChange={e => updateField('requirements', e.target.value || undefined)}
                />
              </div>

              <div>
                <label htmlFor="responsibilities" className="block text-sm font-medium text-gray-700 mb-2">
                  Responsibilities
                </label>
                <textarea
                  id="responsibilities"
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="What will the candidate be doing day-to-day?"
                  value={form.responsibilities || ''}
                  onChange={e => updateField('responsibilities', e.target.value || undefined)}
                />
              </div>

              <div>
                <label htmlFor="benefits" className="block text-sm font-medium text-gray-700 mb-2">
                  Benefits
                </label>
                <textarea
                  id="benefits"
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="What benefits do you offer?"
                  value={form.benefits || ''}
                  onChange={e => updateField('benefits', e.target.value || undefined)}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Link
              to="/company/jobs"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save as Draft'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={e => handleSubmit(e as any, true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? 'Publishing...' : 'Save & Publish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
